import { useMemo, useState } from "react";
import { Card, Tag, Tooltip, Empty, Popconfirm, Segmented } from "antd";
import {
  LockOutlined,
  UnlockOutlined,
  SwapOutlined,
  EditOutlined,
  StopOutlined,
  ReloadOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjsTZ from "../../../utils/dayjs";
import { WEEK_STATUS } from "../aquamanConstants";

// The week-by-week timeline plus every per-week override an admin can apply.
const WeekTimeline = ({
  weeks,
  preview,
  saving,
  onWeekAction,
  onOpenReassign,
  onOpenSwap,
}) => {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [range, setRange] = useState("upcoming");

  // A week counts as "in vijim" until its Sunday is over, so the week in
  // progress always stays visible in the default view.
  const isOver = (week) => week.weekEnd + 86_399_999 < Date.now();

  const rows = useMemo(() => {
    const source = preview || weeks || [];
    const sorted = [...source].sort((a, b) => a.weekStart - b.weekStart);
    if (range === "all") return sorted;
    if (range === "past") return sorted.filter(isOver);
    return sorted.filter((w) => !isOver(w));
  }, [weeks, preview, range]);

  const isCurrent = (week) => {
    const now = Date.now();
    return now >= week.weekStart && now <= week.weekEnd + 86_399_999;
  };

  return (
    <Card
      className={`aquamanCard ${isDarkMode ? "dark" : ""}`}
      title={preview ? "Parashikim (nuk është ruajtur)" : "Kalendari i javëve"}
      extra={
        <Segmented
          size="small"
          value={range}
          onChange={setRange}
          options={[
            { value: "upcoming", label: "Në vijim" },
            { value: "past", label: "Të kaluara" },
            { value: "all", label: "Të gjitha" },
          ]}
        />
      }
    >
      {rows.length === 0 ? (
        <Empty description="Nuk ka javë të planifikuara" />
      ) : (
        <div className="aquamanTimeline">
          {rows.map((week) => {
            const status = WEEK_STATUS[week.status] || WEEK_STATUS.unassigned;
            return (
              <div
                key={week.weekStart}
                className={`aquamanWeekRow ${isCurrent(week) ? "current" : ""}`}
              >
                <span className="range">
                  {dayjsTZ(week.weekStart).format("DD/MM")} -{" "}
                  {dayjsTZ(week.weekEnd).format("DD/MM/YYYY")}
                </span>
                <span className="assignee">
                  {week.employeeName || "—"}
                  {week.locked ? (
                    <Tooltip title="Javë e kyçur: gjenerimi nuk e ndryshon.">
                      <LockOutlined />
                    </Tooltip>
                  ) : null}
                </span>
                <span className="status">
                  <Tag color={status.color}>{status.label}</Tag>
                  {week.origin === "manual" ? <Tag>manual</Tag> : null}
                  {week.origin === "history" ? <Tag>historik</Tag> : null}
                  {week.reason === "returned_from_leave" ? (
                    <Tooltip title="Merr javën e zëvendësuesit pas kthimit nga leja.">
                      <Tag color="blue">kthim nga leja</Tag>
                    </Tooltip>
                  ) : null}
                </span>
                {preview ? null : (
                  <span className="actions">
                    <Tooltip title="Cakto person tjetër">
                      <EditOutlined onClick={() => onOpenReassign(week)} />
                    </Tooltip>
                    <Tooltip title="Ndërro javë me një tjetër">
                      <SwapOutlined onClick={() => onOpenSwap(week)} />
                    </Tooltip>
                    <Tooltip title={week.locked ? "Hap javën" : "Kyç javën"}>
                      {week.locked ? (
                        <UnlockOutlined
                          onClick={() =>
                            onWeekAction(
                              { weekStart: week.weekStart, action: "unlock" },
                              "Java u hap."
                            )
                          }
                        />
                      ) : (
                        <LockOutlined
                          onClick={() =>
                            onWeekAction(
                              { weekStart: week.weekStart, action: "lock" },
                              "Java u kyç."
                            )
                          }
                        />
                      )}
                    </Tooltip>
                    <Tooltip title="Pa shërbim këtë javë">
                      <Popconfirm
                        classNames={{
                          root: `globalPopover ${isDarkMode ? "dark" : ""}`,
                        }}
                        title="Java kalon pa Aquaman?"
                        okText="Po"
                        cancelText="Anulo"
                        disabled={saving}
                        onConfirm={() =>
                          onWeekAction(
                            { weekStart: week.weekStart, action: "skip" },
                            "Java u shënua pa shërbim."
                          )
                        }
                      >
                        <StopOutlined />
                      </Popconfirm>
                    </Tooltip>
                    <Tooltip title="Hiq ndryshimin manual dhe lëri gjenerimin të vendosë">
                      <ReloadOutlined
                        onClick={() =>
                          onWeekAction(
                            { weekStart: week.weekStart, action: "clear" },
                            "Java u rikthye në automatik."
                          )
                        }
                      />
                    </Tooltip>
                    <Tooltip title="Dërgo njoftimin sërish vetëm personit të caktuar">
                      <BellOutlined
                        onClick={() =>
                          onWeekAction(
                            { weekStart: week.weekStart, action: "notify" },
                            "Njoftimi u dërgua."
                          )
                        }
                      />
                    </Tooltip>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default WeekTimeline;
